import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Redirect } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import RefreshIcon from '@material-ui/icons/Refresh';
import BackIcon from '@material-ui/icons/KeyboardArrowLeft';
import withHelp from 'grb-webui/components/withHelp';
import './AppMainPage.scss';

const DEFAULT_VARIANT = 'default';
const FIT_TO_PAGE_VARIANT = 'fit';

const getBackLabel = ({ location, routeService }) => {
  try {
    const { state = {} } = location || {};
    const { referrer } = state || {};
    if (referrer && routeService) {
      // referrer can be a string
      const { pathname = referrer } = referrer;
      const route = pathname && routeService.getMatchingRoute(null, referrer);
      if (route && route.label) {
        return `Back to ${route.label}`;
      }
    }
  } catch (e) {
    console.error(e);
  }
  return 'Back';
};

const makeHelp = (help) => {
  return withHelp(({ helpNode }) => (
    <div className="grb-main-page-help-icon">
      {helpNode}
    </div>
  ), help);
};

const AppMainPage = (props) => {
  const {
    children,
    TrailingBar,
    route,
    variant,
    className,
    rowCount,
    renderIcon,
    onRefresh,
    refreshButtonTooltip,
    refreshButtonId,
    refreshButtonDisabled,
    backTo,
    location,
    help,
    getDefaultReferrer,
  } = props;
  const Icon = !renderIcon && (props.Icon || route.icon);
  const [pageTitle, setPageTitle] = useState(props.pageTitle || route.pageLabel || route.label);
  const [redirectToReferrer, setRedirectToReferrer] = useState(false);
  const [backLabel, setBackLabel] = useState(() => (backTo ? getBackLabel(props) : 'Back'));
  const HelpComp = useMemo(() => makeHelp(help), [help]);

  const handleBack = useCallback(() => {
    setRedirectToReferrer(true);
  }, []);

  useEffect(() => {
    const subscriber = rowCount && rowCount.subscribeSuffiexedLabel(props.pageTitle || route.pageLabel || route.label, (label) => {
      // Workaround to Warning: Cannot update a component from inside the function body of a different component.
      // https://github.com/facebook/react/issues/18178
      setTimeout(() => { setPageTitle(label); }, 0);
    });
    if (rowCount) {
      setPageTitle(rowCount.computeLabel(props.pageTitle || route.pageLabel || route.label));
    } else {
      setPageTitle(props.pageTitle || route.pageLabel || route.label);
    }
    return () => {
      if (subscriber) {
        rowCount.unsubscribe(subscriber);
      }
    };
  }, [rowCount, props.pageTitle]);

  const handleRefresh = useCallback(() => {
    onRefresh();
  }, [onRefresh]);

  useEffect(() => {
    if (backTo) {
      setBackLabel(getBackLabel({ ...props, location }));
    }
  }, [backTo, location]);

  if (redirectToReferrer) {
    return <Redirect to={location.state && location.state.referrer || (getDefaultReferrer ? getDefaultReferrer(props) : { pathname: '/' })} />;
  }

  return (
    <div className={classnames('grb-main-page',
        variant && `grb-main-page-${variant}`,
        backTo && 'grb-main-page-with-back',
        className)}
    >
      {!!location && backTo && (
        <div className="grb-main-page-back-to-row">
          <Button onClick={handleBack} id="grb-back-button">
            <BackIcon className="grb-lefticon" />
            {backLabel}
          </Button>
        </div>
      )}
      <header className="grb-main-page-header">
        {renderIcon && renderIcon()}
        {!renderIcon && !!Icon && (
          <Icon className="grb-main-page-header-icon" style={{ fontSize: 40 }} />
        )}
        <Typography variant="h5" className="grb-main-page-title">{pageTitle}</Typography>
        {!!HelpComp && <HelpComp />}
        {(TrailingBar || !!onRefresh) && (
          <React.Fragment>
            <div className="grb-main-page-header-flex" />
            <div className="grb-main-page-toolbar">
              {TrailingBar}
              {!!onRefresh && (
                <div className="grb-main-page-header-buttons">
                  <Tooltip title={refreshButtonTooltip || 'Refresh this page'}>
                    <div className="grb-main-page-header-refresh-btn-bg">
                      <Button
                        aria-label="refresh"
                        onClick={handleRefresh}
                        id={refreshButtonId || 'update-page'}
                        className="grb-main-page-header-refresh-btn"
                        disabled={refreshButtonDisabled}
                        variant={TrailingBar ? 'outlined' : undefined}
                      >
                        <RefreshIcon />
                      </Button>
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
          </React.Fragment>
        )}
      </header>
      <main className="grb-main-page-body">
        {children}
      </main>
    </div>
  );
};

AppMainPage.propTypes = {
  route: PropTypes.shape({
    label: PropTypes.string,
    pageLabel: PropTypes.string,
    icon: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({}),
    ]),
  }).isRequired,
  variant: PropTypes.string,
  children: PropTypes.node.isRequired,
  TrailingBar: PropTypes.node,
  className: PropTypes.string,
  Icon: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({}),
  ]),
  renderIcon: PropTypes.func,
  rowCount: PropTypes.shape({
    computeLabel: PropTypes.func,
    subscribeSuffiexedLabel: PropTypes.func,
    unsubscribe: PropTypes.func,
  }),
  pageTitle: PropTypes.string,
  onRefresh: PropTypes.func,
  refreshButtonTooltip: PropTypes.string,
  refreshButtonId: PropTypes.string,
  refreshButtonDisabled: PropTypes.bool,
  backTo: PropTypes.bool,
  location: PropTypes.shape({
    state: PropTypes.shape({
      referrer: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({}),
      ]),
    }),
  }),
  getDefaultReferrer: PropTypes.func,
  routeService: PropTypes.shape({
    getMatchingRoute: PropTypes.func,
  }),
  help: PropTypes.shape({}),
};

AppMainPage.defaultProps = {
  variant: DEFAULT_VARIANT,
  TrailingBar: undefined,
  className: undefined,
  Icon: undefined,
  renderIcon: undefined,
  rowCount: undefined,
  pageTitle: undefined,
  onRefresh: undefined,
  refreshButtonTooltip: undefined,
  refreshButtonId: undefined,
  refreshButtonDisabled: undefined,
  backTo: undefined,
  location: undefined,
  getDefaultReferrer: undefined,
  routeService: undefined,
  help: undefined,
};

export { AppMainPage as default, DEFAULT_VARIANT, FIT_TO_PAGE_VARIANT };
